import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid
} from '@mui/material';
import { t } from '../../utils/localization/localization';
import { PriorityBadge, TimeframeBadge } from './RecommendationBadges';
import { logger } from '../../utils/logger';

/**
 * Component for rendering a recommendations section
 */
const RecommendationSection = ({ titleKey, recommendations }) => {
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  logger.info(`[RecommendationSection] Rendering ${titleKey} with ${recommendations.length} items`);

  // Get section title and description
  const getSectionInfo = () => {
    switch (titleKey) {
      case 'immediateRecommendations':
        return {
          title: t('result.immediateRecommendations') || 'Immediate Training Recommendations',
          description: t('result.immediateDescription') || 'High-priority trainings to address critical skill gaps'
        };
      case 'futureRecommendations':
        return {
          title: t('result.futureRecommendations') || 'Future Development Opportunities',
          description: t('result.futureDescription') || 'Strategic trainings for long-term career growth'
        };
      case 'aiRecommendations':
        return {
          title: t('result.aiRecommendations') || 'AI & Automation Readiness',
          description: t('result.aiDescription') || 'Trainings to stay competitive in an AI-driven future'
        };
      default:
        return {
          title: titleKey,
          description: ''
        };
    }
  };

  const { title, description } = getSectionInfo();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {description}
          </Typography>
        )}

        <Grid container spacing={3}>
          {recommendations.map((rec, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div" gutterBottom>
                    {rec.recommendation || rec.title || t('result.untitledRecommendation')}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <PriorityBadge priority={rec.priority} />
                    <TimeframeBadge timeframe={rec.timeframe} />
                  </Box>

                  {rec.reason && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="primary">
                        {t('result.reason') || 'Why this matters:'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rec.reason}
                      </Typography>
                    </Box>
                  )}

                  {rec.suggested_resources && rec.suggested_resources.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        {t('result.suggestedResources') || 'Suggested Resources:'}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {rec.suggested_resources.map((resource, resIndex) => (
                          <Typography variant="body2" color="text.secondary" key={resIndex} sx={{ mb: 0.5 }}>
                            • {resource}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RecommendationSection;
